import maxTagDimensions from '@flame-frontend-utils/tiptap/configs/river/maxTagDimensions.json';
import { ReactNode } from 'react';
import { getResizedImageSrcSet, getResizedImageUrl } from '@flame-frontend-utils/tiptap';
import { Duration, intervalToDuration } from 'date-fns';
import noun from 'plural-ru';
import { RiverCustomFieldsSchema } from '@flame-frontend-utils/commons';
import { Page } from '../../components/Page';
import { tw } from '../../styles/tw';
import { layout } from '../../styles/layout';
import { MediaPostListByTagQuery } from './gql/MediaPostListByTag.document';
import { ROUTES } from '../../lib/ROUTES';

interface TagDetailPageProps {
  tag: NonNullable<MediaPostListByTagQuery['tag']>;
  children?: ReactNode;
}

const MAX_IMAGE_WIDTH = maxTagDimensions.horizontal.width;
const MAX_IMAGE_HEIGHT = maxTagDimensions.horizontal.height;

const TagDetailPage = ({ tag, children }: TagDetailPageProps) => {
  const customFields = RiverCustomFieldsSchema.parse(tag.customFields) ?? {};

  const ageString = getAgeString(customFields.birthDate);
  const hasImage = Boolean(customFields.image?.originalUrl.trim());

  const hasExtras = Boolean(customFields.description) || Boolean(ageString) || hasImage;

  /*
   * No description, no birthday, no image
   * → render one small, centered header block with tag name
   */

  if (!hasExtras) {
    return (
      <Page
        className={tw(layout, 'mx-auto my-0 justify-start')}
        documentTitle={tag.value}
        canonicalUrl={ROUTES.TAG_FILTER.$buildPath({ params: { id: tag.alias } })}
        overflowY="scroll"
        hideAdvertisement
      >
        <div className="justify-center py-2">
          <h1 className="m-0 border-b border-blood text-center text-xl">{tag.value}</h1>
        </div>

        {children}
      </Page>
    );
  }

  /*
   * image + description or birthday,
   * → render 2 column layout
   */
  return (
    <Page
      className={tw(layout, 'mx-auto my-0 flex-col justify-start')}
      documentTitle={tag.value}
      canonicalUrl={ROUTES.TAG_FILTER.$buildPath({ params: { id: tag.alias } })}
      overflowY="scroll"
      hideAdvertisement
    >
      <div className="mb-6 grid border-b border-blood pb-6 pt-5 [grid-template-areas:'header''image''description''birthday'] lg:grid-cols-2 lg:pt-6 lg:[grid-template-areas:'header_image''description_image''birthday_image'] lg:[grid-template-rows:auto_auto_auto]">
        {/* header (always) */}
        <h1 className="mb-6 text-xl [grid-area:header] lg:ml-6">{tag.value}</h1>

        {/* birthday (optional) */}
        {ageString && <p className="mt-2 [grid-area:birthday] lg:ml-6">{ageString}</p>}

        {/* description (optional) */}
        {customFields.description && (
          <p className="mb-6 min-w-0 whitespace-pre-line break-words [grid-area:description] lg:ml-6">
            {customFields.description}
          </p>
        )}

        {/* image (optional) */}
        {hasImage && customFields.image && (
          <div className="mb-6 justify-self-center [grid-area:image] lg:mb-0 lg:justify-self-center lg:pr-6">
            <img
              loading="lazy"
              alt={customFields.image.alt ?? tag.value}
              width={MAX_IMAGE_WIDTH}
              height={MAX_IMAGE_HEIGHT}
              src={getResizedImageUrl({
                resizeOrigin: PUBLIC_CONFIG.IMAGE_RESIZE_ORIGIN,
                originalUrl: customFields.image.originalUrl,
                cropSettings: isCropSettingsMap(customFields.image.cropSettings)
                  ? customFields.image.cropSettings.horizontal
                  : undefined,
                width: MAX_IMAGE_WIDTH,
                height: MAX_IMAGE_HEIGHT,
              })}
              srcSet={getResizedImageSrcSet({
                resizeOrigin: PUBLIC_CONFIG.IMAGE_RESIZE_ORIGIN,
                originalUrl: customFields.image.originalUrl,
                cropSettings: isCropSettingsMap(customFields.image.cropSettings)
                  ? customFields.image.cropSettings.horizontal
                  : undefined,
                width: MAX_IMAGE_WIDTH,
                height: MAX_IMAGE_HEIGHT,
              })}
            />
          </div>
        )}
      </div>

      {children}
    </Page>
  );
};

function isCropSettingsMap(map: unknown): map is Record<keyof typeof maxTagDimensions, unknown> {
  return Boolean(map && typeof map === 'object');
}

function getAgeString(isoDate: string | null | undefined) {
  if (!isoDate) {
    return null;
  }

  const { years, months, days } = getAge(isoDate);
  let ageString: string | null = null;

  if (years && years > 0) {
    ageString = `${years} ${noun(years, 'год', 'года', 'лет')}`;
  } else if (months && months > 0) {
    ageString = `${months} ${noun(months, 'месяц', 'месяца', 'месяцев')}`;
  } else if (days && days > 0) {
    ageString = `${days} ${noun(days, 'день', 'дня', 'дней')}`;
  }

  return ageString;
}

function getAge(isoDate: string): Duration {
  const birthDate = getStartOfDayInLocalTime(isoDate);
  const today = new Date();

  return intervalToDuration({
    start: birthDate,
    end: today,
  });
}

function getStartOfDayInLocalTime(isoDate: string): Date {
  const parts = isoDate.split('-');

  const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  date.setHours(0, 0, 0, 0);

  return date;
}

export { TagDetailPage };
